import { z } from 'zod';

// "HH:MM" 24-hour wall-clock validator for the workday break windows.
//
// STRICT on purpose, and exported so nobody writes a second, looser one. The
// obvious `\d{1,2}:\d{2}` accepts "25:99", which reaches
// `combineDateAndZonedTime` and THROWS a RangeError — one hand-edited value then
// takes down every rollup for that company. `employee.schema.ts` imports this
// for the per-worker `workStartTime` override so the two ends of the same
// precedence chain (`employee.workStartTime ?? defaultWorkday.shiftStartTime`)
// cannot disagree about which strings are times. It lives here, beside the one
// group in this module, so `settings.schema.ts` can re-export it without either
// file importing the other in a cycle.
export const HHMM_RE = /^([01]?\d|2[0-3]):[0-5]\d$/u;

/**
 * THE SITE WORKERS' DAY (owner, 2026-10-20: "build for construction sites +
 * workers"). Applies to `workforceType === 'SITE_WORKER'` on a WORKING day;
 * office staff, rest days and public holidays keep the rules they had.
 *
 *   The day starts whenever they check in — the buses decide arrival, so
 *   there is no fixed start and no LATE / EARLY_LEAVE against a clock.
 *   worked  = time on site − its overlap with the day's break window (the
 *             existing `normalBreakWindow` / summer window — no flat hour)
 *   normal  = min(worked, `paidHours`)        less is a shortfall, paid as is
 *   OT      = whole `overtimeUnitMinutes` past `paidHours`, rounded DOWN,
 *             capped by `overtime.maxDailyMinutes`
 *
 * A day nobody checked out of is closed at `missedCheckoutCloseAt` the NEXT
 * company morning, FLAGGED, and pays `missedCheckoutPays`. Night workers are
 * never closed this way. The pure rule is `utils/siteWorkerDay.ts` in the API.
 *
 * NESTED UNDER `defaultWorkday` so it is part of the pay policy snapshot and of
 * the derivation categories by construction: a save mints a policy version for
 * the day it is made, and "Recompute from date" is the deliberate act that
 * reaches further back. The rollup reads the RAW stored block — absent means
 * the rule is not in force — so a company whose settings were last saved
 * before this group existed is not re-priced by a read.
 */
export const SiteWorkerDaySchema = z.object({
  // OFF by default: a save of any other workday setting materialises this
  // block, and it must not move every site worker onto a new pay rule by the
  // side door. The owner turns it on here, with one switch.
  enabled: z.boolean().default(false),
  /** Paid normal hours. Past this is overtime; short of it is a shortfall. */
  paidHours: z.number().min(1).max(16).default(9),
  /** Overtime is paid in whole units of this, rounded down — the grace. */
  overtimeUnitMinutes: z.number().int().min(1).max(120).default(60),
  /** Company time, on the morning AFTER the day, that a missed check-out is closed. */
  missedCheckoutCloseAt: z.string().regex(HHMM_RE, 'Expected HH:MM').default('02:00'),
  /** The closed day pays `paidHours` and no overtime. One option, stated rather than implied. */
  missedCheckoutPays: z.enum(['NORMAL_ONLY']).default('NORMAL_ONLY'),
});
export type SiteWorkerDay = z.infer<typeof SiteWorkerDaySchema>;

/** Is the site workers' rule in force under the API's raw read, and why not? */
export interface SiteWorkerDayStatus {
  active: boolean;
  /** First day of the unbroken run of policy versions carrying the rule, `YYYY-MM-DD`; null when off. */
  activeSince: string | null;
  /**
   * - `active` — saved and switched on.
   * - `off` — saved and switched off.
   * - `not-saved` — the settings were last saved before this group existed, so
   *   what the screen shows is the default and nothing is computed from it.
   * - `no-workday` — the company has no standard day stored at all, and the
   *   rule rides on it.
   */
  reason: 'active' | 'off' | 'not-saved' | 'no-workday';
}

/** What "Recompute from date" did. */
export interface SiteWorkerDayApplyResult {
  /** First day the rule now applies to, `YYYY-MM-DD`. */
  from: string;
  /** Last day marked, today in the company zone. */
  to: string;
  /** Stored days queued for the recompute drain. */
  daysMarked: number;
  /** Pay policy versions that now carry the rule from `from`. */
  versionsUpdated: number;
  /** Versions left alone because they hold no standard day. */
  versionsSkipped: number;
}
